import React, { useEffect, useRef, useState } from "react";
import { FaSignOutAlt } from "react-icons/fa";
import { RiShieldUserLine } from "react-icons/ri";
import { HiOutlineCog, HiOutlineUserCircle } from "react-icons/hi";

import { Avatar, Badge, cn } from "../../ui";

type Props = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  onSignOut: () => void;
};

const prettifyRole = (role?: string) =>
  (role ?? "").replace(/_/g, " ").toLowerCase();

const UserMenu: React.FC<Props> = ({
  firstName,
  lastName,
  email,
  role,
  onSignOut,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "—";
  const prettyRole = prettifyRole(role);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "group inline-flex items-center gap-2.5 rounded-full border bg-white py-1 pl-1 pr-2.5 transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
          open
            ? "border-brand/30 shadow-[0_0_0_4px_rgba(0,145,145,0.06)]"
            : "border-gray-200 hover:border-gray-300",
        )}
      >
        <Avatar firstName={firstName} lastName={lastName} size="sm" />
        <span className="hidden text-left leading-tight sm:block">
          <span
            className="block max-w-[10rem] truncate text-[12.5px] font-medium text-gray-900"
            title={fullName}
          >
            {fullName}
          </span>
          <span className="block truncate text-[10.5px] capitalize text-gray-500">
            {prettyRole || "member"}
          </span>
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_20px_60px_-20px_rgba(16,24,40,0.25)]"
        >
          {/* Identity */}
          <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-4">
            <Avatar firstName={firstName} lastName={lastName} size="md" />
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-[13px] font-medium text-gray-900"
                title={fullName}
              >
                {fullName}
              </p>
              {email ? (
                <p
                  className="truncate text-[11.5px] text-gray-500"
                  title={email}
                >
                  {email}
                </p>
              ) : null}
              <Badge
                tone="brand"
                size="sm"
                leadingIcon={<RiShieldUserLine className="h-3 w-3" />}
                className="mt-2 self-start capitalize"
              >
                {prettyRole || "member"}
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <nav className="py-1.5">
            <MenuItem
              icon={<HiOutlineUserCircle className="h-4 w-4" />}
              label="Profile"
              hint="View your profile"
              disabled
            />
            <MenuItem
              icon={<HiOutlineCog className="h-4 w-4" />}
              label="Settings"
              hint="Password, preferences"
              disabled
            />
            <div className="my-1 h-px bg-gray-100" />
            <MenuItem
              icon={<FaSignOutAlt className="h-3.5 w-3.5" />}
              label="Sign out"
              hint="End this session"
              tone="destructive"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            />
          </nav>
        </div>
      ) : null}
    </div>
  );
};

const MenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint?: string;
  disabled?: boolean;
  tone?: "neutral" | "destructive";
  onClick?: () => void;
}> = ({ icon, label, hint, disabled, tone = "neutral", onClick }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex w-full items-center gap-3 px-4 py-2 text-left transition-colors",
      "focus:outline-none focus-visible:bg-gray-50",
      disabled
        ? "cursor-not-allowed opacity-50"
        : tone === "destructive"
        ? "hover:bg-red-50"
        : "hover:bg-gray-50",
    )}
  >
    <span
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ring-inset",
        tone === "destructive"
          ? "bg-red-50 text-red-600 ring-red-100"
          : "bg-gray-50 text-gray-600 ring-gray-200",
      )}
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span
        className={cn(
          "block text-[12.5px] font-medium",
          tone === "destructive" ? "text-red-700" : "text-gray-900",
        )}
      >
        {label}
      </span>
      {hint ? (
        <span className="block truncate text-[10.5px] text-gray-500">
          {hint}
        </span>
      ) : null}
    </span>
  </button>
);

export default UserMenu;
